import { Suspense, lazy, useRef } from "react";
import { m, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Shield, Star, Users } from "lucide-react";
import { Link } from "react-router-dom";
import SharedNavbar from "@/components/shared/SharedNavbar";
import SharedFooter from "@/components/shared/SharedFooter";
import SEOHead, {
  buildFaqSchema,
  buildBreadcrumbSchema,
  buildLocalBusinessSchema,
  buildPricingProductSchema,
} from "@/components/shared/SEOHead";
import { AppStoreBadges } from "@/components/shared/AppStoreBadges";
import { LazyOnVisible } from "@/components/shared/LazyOnVisible";
import Orb from "@/components/home/Orb";
import { useI18n } from "@/contexts/I18nContext";

/** True when OS/browser requests reduced motion. Evaluated once at module load (SSR-safe). */
const PREFERS_REDUCED_MOTION: boolean =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Lazy below-the-fold: ogni sezione in chunk separato — Home chunk iniziale -37KB.
const LaptopDemo = lazy(() => import("@/components/home/LaptopDemo"));
const FeaturesSection = lazy(() => import("@/components/home/FeaturesSection"));
const PricingSection = lazy(() => import("@/components/home/PricingSection"));
const TestimonialsSection = lazy(() => import("@/components/home/TestimonialsSection"));
const ContactFormSection = lazy(() => import("@/components/home/ContactFormSection"));

const SectionSkeleton = () => <div className="min-h-[320px]" aria-hidden="true" />;

/* ─── Hero ─── */
function HeroSection() {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // PRM: freeze at neutral values — no parallax scroll motion when reduced motion is requested.
  const textY = useTransform(scrollYProgress, [0, 0.5], PREFERS_REDUCED_MOTION ? [0, 0] : [0, -200]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], PREFERS_REDUCED_MOTION ? [1, 1] : [1, 0]);

  return (
    // id="hero-sentinel" used by MobileStickyCTA IntersectionObserver to detect hero visibility.
    <section id="hero-sentinel" ref={sectionRef} className="relative min-h-screen overflow-hidden pt-[72px] bg-noise">
      {/* Overlay più trasparente in bottom: aurora WebGL deve restare visibile sotto */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-transparent to-background/60" />

      <Orb
        className="animate-float-slow w-[300px] h-[300px] md:w-[600px] md:h-[600px] -top-40 -left-40 opacity-20"
        style={{ background: "radial-gradient(circle, hsl(197 82% 61% / 0.3), transparent 70%)" }}
      />
      <Orb
        className="animate-float-slow-reverse w-[260px] h-[260px] md:w-[500px] md:h-[500px] top-20 -right-40 opacity-15"
        style={{ background: "radial-gradient(circle, hsl(204 85% 43% / 0.3), transparent 70%)" }}
      />
      <Orb
        className="animate-float-slow w-[220px] h-[220px] md:w-[400px] md:h-[400px] bottom-40 left-1/4 opacity-10"
        style={{ background: "radial-gradient(circle, hsl(195 100% 74% / 0.25), transparent 70%)" }}
      />

      <div className="absolute inset-0 bg-grid opacity-60" />

      <m.div
        className="relative z-10 flex flex-col items-center text-center mt-16 md:mt-24 px-4"
        style={{ y: textY, opacity: textOpacity }}
      >
        <m.div
          className="liquid-glass flex items-center gap-2 px-3 py-2 rounded-lg mb-6"
          initial={PREFERS_REDUCED_MOTION ? false : { opacity: 0, y: 10, filter: "blur(10px)" }}
          animate={PREFERS_REDUCED_MOTION ? false : { opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.6 }}
        >
          <span className="bg-primary text-primary-foreground rounded-md text-xs font-semibold px-2 py-0.5">{t("site.index.hero.badge_new")}</span>
          <span className="text-sm font-medium text-muted-foreground">{t("site.index.hero.badge_label")} <span className="italic text-primary">{t("site.index.hero.badge_label_italic")}</span></span>
        </m.div>

        <m.h1
          className="text-5xl md:text-7xl tracking-[-2px] font-bold leading-tight md:leading-[1.1] mb-4"
          initial={PREFERS_REDUCED_MOTION ? false : { opacity: 0, y: 30, filter: "blur(10px)" }}
          animate={PREFERS_REDUCED_MOTION ? false : { opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          {t("site.index.hero.title_prefix")}{" "}
          <span className="font-serif italic font-normal text-primary">{t("site.index.hero.title_italic")}</span>
        </m.h1>

        <m.p
          className="text-lg font-normal leading-7 mb-8 max-w-xl text-muted-foreground"
          initial={PREFERS_REDUCED_MOTION ? false : { opacity: 0, y: 30, filter: "blur(10px)" }}
          animate={PREFERS_REDUCED_MOTION ? false : { opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {t("site.index.hero.sub")}
        </m.p>

        <m.div
          className="flex gap-4 flex-wrap justify-center"
          initial={PREFERS_REDUCED_MOTION ? false : { opacity: 0, y: 30, filter: "blur(10px)" }}
          animate={PREFERS_REDUCED_MOTION ? false : { opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Link
            to="/registrazione"
            className="relative bg-primary text-primary-foreground rounded-full px-8 py-3.5 text-base font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.03] active:scale-[0.98] transition-[transform,box-shadow] duration-200 inline-flex items-center gap-2"
          >
            {t("site.index.hero.cta_primary")} <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href={`${(import.meta.env.VITE_APP_DASHBOARD_URL as string) || "https://app.swissez.ch"}/api/demo/sso`}
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-emerald-500/50 text-emerald-600 dark:text-emerald-400 rounded-full px-8 py-3.5 text-base font-semibold hover:bg-emerald-500/10 hover:scale-[1.03] active:scale-[0.98] transition-[transform,colors,background] duration-200 inline-flex items-center gap-2"
          >
            {t("site.index.hero.cta_demo")} <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#come-funziona"
            className="border border-border/60 text-foreground rounded-full px-8 py-3.5 text-base font-semibold hover:border-primary hover:text-primary hover:scale-[1.03] active:scale-[0.98] transition-[transform,colors] duration-200"
          >
            {t("site.index.hero.cta_secondary")}
          </a>
        </m.div>

        <m.div
          className="flex items-center gap-8 mt-12 text-muted-foreground/50 text-xs uppercase tracking-widest"
          initial={PREFERS_REDUCED_MOTION ? false : { opacity: 0 }}
          animate={PREFERS_REDUCED_MOTION ? false : { opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          <span className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> {t("site.index.hero.trust_data")}
          </span>
          <span className="hidden md:flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> {t("site.index.hero.trust_clients")}
          </span>
          <span className="hidden md:flex items-center gap-2">
            <Star className="w-3.5 h-3.5" /> {t("site.index.hero.trust_rating")}
          </span>
        </m.div>
      </m.div>

      {/* Bottom fade ridotto: solo 30vh + opacity 0.5 max → aurora visibile sotto */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 z-30 pointer-events-none"
        style={{
          background: "linear-gradient(to top, hsl(var(--background) / 0.5), transparent)",
        }}
      />
    </section>
  );
}

/* ─── Come Funziona ─── */
function StepsSection() {
  const { t } = useI18n();
  const steps = [
    { title: t("site.index.steps.1.title"), desc: t("site.index.steps.1.desc"), image: "/step-profilo.webp", w: 1600, h: 898 },
    { title: t("site.index.steps.2.title"), desc: t("site.index.steps.2.desc"), image: "/step-dashboard.webp", w: 1193, h: 896 },
    { title: t("site.index.steps.3.title"), desc: t("site.index.steps.3.desc"), image: "/step-notification.webp", w: 1024, h: 1024 },
    { title: t("site.index.steps.4.title"), desc: t("site.index.steps.4.desc"), image: "/step-dichiarazione.webp", w: 1600, h: 898 },
  ];

  return (
    <section className="relative py-24 md:py-32 px-6 md:px-8 overflow-hidden" id="come-funziona">
      <Orb
        className="animate-float-slow-reverse w-[260px] h-[260px] md:w-[500px] md:h-[500px] -right-60 top-20 opacity-10"
        style={{ background: "radial-gradient(circle, hsl(197 82% 61% / 0.3), transparent 70%)" }}
      />
      <Orb
        className="animate-float-slow w-[220px] h-[220px] md:w-[400px] md:h-[400px] -left-40 bottom-40 opacity-10"
        style={{ background: "radial-gradient(circle, hsl(204 85% 43% / 0.2), transparent 70%)" }}
      />

      <div className="max-w-[1100px] mx-auto relative z-10">
        <m.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4 border border-primary/20">
            {t("site.index.steps.pill")}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-3">{t("site.index.steps.title")}</h2>
          <p className="text-muted-foreground text-lg">{t("site.index.steps.sub")}</p>
        </m.div>

        <div className="flex flex-col gap-16 md:gap-24">
          {steps.map((step, i) => {
            const isLeft = i % 2 === 0;
            return (
              <m.div
                key={i}
                className={`flex flex-col md:flex-row items-center gap-8 md:gap-14 ${!isLeft ? "md:flex-row-reverse" : ""}`}
                initial={{ opacity: 0, x: isLeft ? -60 : 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
              >
                <div className="flex-1 flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <m.div
                      className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-xl md:text-2xl font-bold text-primary-foreground shadow-lg shadow-primary/30"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {i + 1}
                    </m.div>
                  </div>
                  <div className="liquid-glass card-shine rounded-2xl p-6 md:p-8 flex-1">
                    <h3 className="text-lg font-bold mb-2 text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </div>

                {step.image ? (
                  <m.div
                    className="flex-shrink-0 w-full md:w-[320px]"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <div className="relative group">
                      <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-primary/10 to-accent/5 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <img
                        src={step.image}
                        alt={step.title}
                        width={step.w}
                        height={step.h}
                        loading="lazy"
                        decoding="async"
                        className="relative w-full h-auto rounded-2xl border border-border/30 shadow-xl shadow-primary/5 group-hover:shadow-2xl group-hover:shadow-primary/10 transition-shadow duration-500"
                      />
                    </div>
                  </m.div>
                ) : (
                  <div className="hidden md:block w-[320px]" />
                )}
              </m.div>
            );
          })}
        </div>

        <m.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <a
            href="/registrazione"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-10 py-4 text-base font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/35 transition-shadow duration-300"
          >
            {t("site.index.steps.cta")} <ArrowRight className="w-5 h-5" />
          </a>
        </m.div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function Index() {
  const { t } = useI18n();
  return (
    <main className="overflow-x-hidden">
      <SEOHead
        title={t("site.index.seo.title")}
        description={t("site.index.seo.description")}
        path="/"
        structuredData={[
          buildLocalBusinessSchema(),
          ...buildPricingProductSchema([
            {
              name: "Starter",
              price: "CHF 69",
              currency: "CHF",
              description: "Piano Starter di Swissez — fiduciario digitale per indipendenti e piccole attività in Ticino",
            },
            {
              name: "Business",
              price: "CHF 149",
              currency: "CHF",
              description: "Piano Business di Swissez — fiduciario digitale completo per PMI ticinesi (Sagl/SA)",
            },
            {
              name: "Premium",
              price: "CHF 249",
              currency: "CHF",
              description: "Piano Premium di Swissez — fiduciario digitale enterprise con supporto prioritario e funzionalità avanzate",
            },
          ]),
          buildFaqSchema([
            { question: t("site.faq.q1"), answer: t("site.faq.a1") },
            { question: t("site.faq.q2"), answer: t("site.faq.a2") },
            { question: t("site.faq.q3"), answer: t("site.faq.a3") },
            { question: t("site.faq.q4"), answer: t("site.faq.a4") },
          ]),
          buildBreadcrumbSchema([{ name: "Home", path: "/" }]),
        ]}
      />

      <SharedNavbar />
      <HeroSection />
      <Suspense fallback={<SectionSkeleton />}>
        <LaptopDemo />
      </Suspense>
      <StepsSection />
      <LazyOnVisible fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <FeaturesSection />
        </Suspense>
      </LazyOnVisible>
      <LazyOnVisible fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <PricingSection />
        </Suspense>
      </LazyOnVisible>
      <LazyOnVisible fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <TestimonialsSection />
        </Suspense>
      </LazyOnVisible>
      <LazyOnVisible fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <ContactFormSection />
        </Suspense>
      </LazyOnVisible>
      <AppStoreBadges />
      <SharedFooter />
    </main>
  );
}
